import type { StreamContent } from "@/lib/humhub-api/types";

type RawMatchedEntity = {
  object_model?: unknown;
  objectModel?: unknown;
  object_id?: unknown;
};

type NormalizedMatchedEntity = {
  model: string;
  objectId: number;
};

const parseObjectModel = (value: unknown): string => {
  const raw =
    typeof value === "string" ? value : value == null ? "" : String(value);
  return raw.trim().toLowerCase().replace(/\\/g, "/");
};

const normalizeEntityModel = (objectModel: string): string => {
  if (!objectModel) return "";
  const normalized = objectModel.trim().toLowerCase().replace(/\\/g, "/");
  const parts = normalized.split("/");
  return parts.pop() || normalized;
};

const parseMatchedId = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;

  const normalized =
    typeof value === "number" ? value.toString() : String(value).trim();

  if (!normalized) return null;

  const parsed = Number(normalized);

  if (!Number.isInteger(parsed) || !Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

const normalizeMatchedEntity = (
  entry: unknown,
): NormalizedMatchedEntity | null => {
  if (!entry || typeof entry !== "object") return null;

  const entity = entry as RawMatchedEntity;
  const objectId = parseMatchedId(entity.object_id);
  if (objectId === null) {
    return null;
  }

  const objectModel = parseObjectModel(
    entity.object_model ?? entity.objectModel,
  );

  if (!objectModel) {
    return null;
  }

  return {
    model: normalizeEntityModel(objectModel),
    objectId,
  };
};

const MATCHED_ENTITY_MODELS = {
  post: "post",
  comment: "comment",
} as const;

const MATCHED_COMMENT_IDS_EMPTY: number[] = [];
Object.freeze(MATCHED_COMMENT_IDS_EMPTY);

type MatchedEntitySummary = {
  commentIds: number[];
  isCurrentPostMatched: boolean;
};

const summarizeMatchedEntities = (
  post: StreamContent,
): MatchedEntitySummary => {
  if (!post.matched_entities?.length) {
    return {
      commentIds: MATCHED_COMMENT_IDS_EMPTY,
      isCurrentPostMatched: false,
    };
  }

  const commentIds: number[] = [];
  const seenCommentIds = new Set<number>();
  let isCurrentPostMatched = false;

  const postTargetIds = new Set<number>(
    [post.id, post.metadata?.object_id]
      .map(parseMatchedId)
      .filter((value): value is number => value !== null),
  );

  for (const entry of post.matched_entities) {
    const normalizedEntity = normalizeMatchedEntity(entry);
    if (!normalizedEntity) {
      continue;
    }

    if (normalizedEntity.model === MATCHED_ENTITY_MODELS.comment) {
      if (!seenCommentIds.has(normalizedEntity.objectId)) {
        seenCommentIds.add(normalizedEntity.objectId);
        commentIds.push(normalizedEntity.objectId);
      }
      continue;
    }

    if (
      normalizedEntity.model === MATCHED_ENTITY_MODELS.post &&
      !isCurrentPostMatched &&
      postTargetIds.has(normalizedEntity.objectId)
    ) {
      isCurrentPostMatched = true;
    }
  }

  return {
    commentIds: commentIds.length > 0 ? commentIds : MATCHED_COMMENT_IDS_EMPTY,
    isCurrentPostMatched,
  };
};

export const getMatchedCommentIds = (post: StreamContent): number[] => {
  return summarizeMatchedEntities(post).commentIds;
};

export type SearchMatchState = {
  matchedCommentIds: number[];
  isCurrentPostMatched: boolean;
  signature: string;
};

export const DEFAULT_SEARCH_MATCH_STATE: SearchMatchState = Object.freeze({
  matchedCommentIds: MATCHED_COMMENT_IDS_EMPTY,
  isCurrentPostMatched: false,
  signature: "0:",
});

export const EMPTY_SEARCH_MATCH_STATE: SearchMatchState =
  DEFAULT_SEARCH_MATCH_STATE;

export const getSearchMatchState = (post: StreamContent): SearchMatchState => {
  const { commentIds, isCurrentPostMatched } = summarizeMatchedEntities(post);

  return {
    matchedCommentIds: commentIds,
    isCurrentPostMatched,
    signature: `${isCurrentPostMatched ? "1" : "0"}:${commentIds.join(",")}`,
  };
};

export const buildSearchMatchStates = (
  streamContents: StreamContent[],
): Map<number, SearchMatchState> =>
  streamContents.reduce((acc, post) => {
    acc.set(post.id, getSearchMatchState(post));
    return acc;
  }, new Map<number, SearchMatchState>());

export const isPostMatched = (post: StreamContent): boolean => {
  return summarizeMatchedEntities(post).isCurrentPostMatched;
};
